import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import elfoMascotLogo from '../../../../assets/elfo-mascot-logo.png';
import { ReactComponent as FilterIcon } from '../../../../assets/icon-filter.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icon-plus.svg';
import { ReactComponent as RefreshIcon } from '../../../../assets/icon-refresh.svg';
import SnippetModal from '../modals/SnippetModal';
import snippetsService from '../../../../services/snippetsService';
import '../../styles/SnippetsPage.scss';

const SnippetsList = ({ wabaId, accessToken }) => {
  // Snippets state management
  const [snippets, setSnippets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSnippets, setFilteredSnippets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'delete'
  const [selectedSnippet, setSelectedSnippet] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  });

  // Load snippets from API
  const loadSnippets = useCallback(async (searchQuery = '') => {
    if (!wabaId || !accessToken) {
      console.warn('Missing wabaId or accessToken for loading snippets');
      setError('Missing authentication or account information. Please refresh the page.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await snippetsService.getSnippets({
        accessToken,
        wabaId,
        search: searchQuery,
        limit: pagination.limit,
        offset: 0 // Reset to first page when searching
      });

      if (response.success) {
        const transformedSnippets = response.data.snippets.map(snippet =>
          snippetsService.transformSnippetFromApi(snippet)
        );

        setSnippets(transformedSnippets);
        setPagination({
          total: response.data.pagination?.total || transformedSnippets.length,
          limit: response.data.pagination?.limit || 50,
          offset: response.data.pagination?.offset || 0,
          hasMore: response.data.pagination?.has_more || false
        });
      } else {
        // Handle specific API error responses
        if (response.message?.includes('Permission denied')) {
          throw new Error('You do not have permission to view snippets for this account');
        } else if (response.message?.includes('not found')) {
          throw new Error('WhatsApp Business Account not found');
        } else {
          throw new Error(response.message || 'Failed to load snippets');
        }
      }
    } catch (error) {
      console.error('Error loading snippets:', error);

      // Handle network errors
      if (error.name === 'NetworkError' || error.message.includes('Network')) {
        setError('Network error. Please check your connection and try again.');
      } else if (error.message.includes('timeout')) {
        setError('Request timed out. Please try again.');
      } else {
        setError(error.message || 'Failed to load snippets. Please try again.');
      }

      setSnippets([]);
    } finally {
      setIsLoading(false);
    }
  }, [wabaId, accessToken, pagination.limit]);

  // Initialize snippets on component mount
  useEffect(() => {
    loadSnippets();
  }, [loadSnippets]);

  // Handle search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        loadSnippets(searchTerm);
      } else {
        loadSnippets();
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, loadSnippets]);

  // Set filtered snippets (now handled by API search)
  useEffect(() => {
    setFilteredSnippets(snippets);
  }, [snippets]);

  // Snippet CRUD operations
  const handleCreateSnippet = async (snippetData) => {
    if (!wabaId || !accessToken) {
      throw new Error('Missing wabaId or accessToken');
    }

    try {
      const apiData = snippetsService.transformSnippetToApi(snippetData);
      const response = await snippetsService.createSnippet(apiData, wabaId, accessToken);

      if (response.success) {
        const newSnippet = snippetsService.transformSnippetFromApi(response.data.snippet);
        setSnippets(prev => [newSnippet, ...prev]);
        return newSnippet;
      } else {
        throw new Error(response.message || 'Failed to create snippet');
      }
    } catch (error) {
      console.error('Error creating snippet:', error);
      throw error;
    }
  };

  const handleEditSnippet = async (snippetData) => {
    if (!accessToken) {
      throw new Error('Missing accessToken');
    }

    try {
      const apiData = snippetsService.transformSnippetToApi(snippetData);
      const response = await snippetsService.updateSnippet(snippetData.id, apiData, accessToken);

      if (response.success) {
        const updatedSnippet = snippetsService.transformSnippetFromApi(response.data.snippet);
        setSnippets(prev => prev.map(snippet =>
          snippet.id === snippetData.id ? updatedSnippet : snippet
        ));
        return updatedSnippet;
      } else {
        throw new Error(response.message || 'Failed to update snippet');
      }
    } catch (error) {
      console.error('Error updating snippet:', error);
      throw error;
    }
  };

  const handleDeleteSnippet = async (snippetId) => {
    if (!accessToken) {
      throw new Error('Missing accessToken');
    }

    try {
      const response = await snippetsService.deleteSnippet(snippetId, accessToken);

      if (response.success) {
        setSnippets(prev => prev.filter(snippet => snippet.id !== snippetId));
        return true;
      } else {
        throw new Error(response.message || 'Failed to delete snippet');
      }
    } catch (error) {
      console.error('Error deleting snippet:', error);
      throw error;
    }
  };

  // Modal handlers
  const openCreateModal = () => {
    setModalMode('create');
    setSelectedSnippet(null);
    setShowModal(true);
  };

  const openEditModal = (snippet) => {
    setModalMode('edit');
    setSelectedSnippet(snippet);
    setShowModal(true);
  };

  const openDeleteModal = (snippet) => {
    setModalMode('delete');
    setSelectedSnippet(snippet);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSnippet(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleRefresh = useCallback(() => {
    setSearchTerm('');
    loadSnippets();
  }, [loadSnippets]);

  // Action button component for DataTable
  const ActionButton = ({ snippet }) => (
    <div className="d-flex justify-content-center align-items-center gap-1">
      <Button
        variant="link"
        className="p-0 me-2"
        onClick={() => openEditModal(snippet)}
        title="Edit"
      >
        <i className="fas fa-edit text-primary"></i>
      </Button>
      <Button
        variant="link"
        className="p-0"
        onClick={() => openDeleteModal(snippet)}
        title="Delete"
      >
        <i className="fas fa-trash text-danger"></i>
      </Button>
    </div>
  );

  // DataTable columns configuration
  const tableColumns = useMemo(
    () => [
      {
        name: 'Snippet Name',
        selector: (row) => row.name,
        sortable: true,
      },
      {
        name: 'Snippet Preview',
        selector: (row) => row.body,
        sortable: false,
        cell: (row) => (
          <div>
            {row.body.length > 50
              ? `${row.body.substring(0, 50)}...`
              : row.body
            }
          </div>
        ),
      },
      {
        name: 'Last Modified',
        selector: (row) => row.lastModified,
        sortable: true,
        cell: (row) => formatDate(row.lastModified),
      },
      {
        name: 'Actions',
        cell: (row) => <ActionButton snippet={row} />,
        width: '120px',
      },
    ],
    []
  );

  return (
    <div className="snippets-container">
      <div className="wa-template-card">
        <div className="card-body p-0">
          {/* Header with search and actions */}
          <div className="d-flex justify-content-end gap-1 mb-4">
            <div className="search-container d-flex mr-auto">
              <div className="search-form position-relative" style={{ flex: 1 }}>
                <FilterIcon className="search-icon" />
                <input
                  type="text"
                  placeholder="Search snippets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="d-flex gap-1">
              <Button
                variant="outline-primary"
                onClick={handleRefresh}
                className="btn-with-icon"
                disabled={isLoading}
              >
                <RefreshIcon className="btn-icon" />
                {isLoading ? 'Loading...' : 'Refresh'}
              </Button>
              <Button
                variant="primary"
                onClick={openCreateModal}
                className="btn-with-icon"
                disabled={isLoading || !wabaId || !accessToken}
              >
                <PlusIcon className="btn-icon" />
                Add New Snippet
              </Button>
            </div>
          </div>

          {/* Error state */}
          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
              <Button
                variant="link"
                className="p-0 ms-2"
                onClick={handleRefresh}
              >
                Try again
              </Button>
            </div>
          )}

          {/* Loading state */}
          {isLoading && filteredSnippets.length === 0 && (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Loading snippets...</p>
              </div>
            </div>
          )}

          {/* Content area */}
          {!error && !isLoading && filteredSnippets.length === 0 ? (
            // Empty state
            <div className="empty-state">
              <div className="empty-state-content">
                <img src={elfoMascotLogo} alt="Elfo Mascot" className="empty-state-logo" />
                <h3 className="empty-state-title">No snippets found</h3>
                <p className="empty-state-description">
                  {searchTerm
                    ? `No snippets match "${searchTerm}". Try a different search term.`
                    : 'You haven\'t created any snippets yet. Create your first snippet to get started.'
                  }
                </p>
                {!searchTerm && wabaId && accessToken && (
                  <Button
                    variant="primary"
                    onClick={openCreateModal}
                    className="empty-state-button btn-with-icon"
                    disabled={isLoading}
                  >
                    <PlusIcon className="btn-icon" />
                    Create Your First Snippet
                  </Button>
                )}
              </div>
            </div>
          ) : !isLoading || filteredSnippets.length > 0 ? (
            // Results state
            <DataTable
              data={filteredSnippets}
              columns={tableColumns}
              progressPending={isLoading && filteredSnippets.length === 0}
              pagination
              paginationTotalRows={pagination.total}
              paginationPerPage={pagination.limit}
              dense
              noDataComponent={
                <div className="text-center py-4">
                  <p className="text-muted">No snippets found matching your search.</p>
                </div>
              }
            />
          ) : null}

          {/* Snippet Modal */}
          <SnippetModal
            show={showModal}
            onHide={closeModal}
            mode={modalMode}
            snippet={selectedSnippet}
            onSave={modalMode === 'edit' ? handleEditSnippet : handleCreateSnippet}
            onDelete={handleDeleteSnippet}
            wabaId={wabaId}
            accessToken={accessToken}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(SnippetsList);
